'use client';
import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Footer from '../Footer';
import { HERO_BOX_IMAGES } from '../../components/hero-animated-stage';
import { HeroTokenCardFace } from '../../components/hero-token-card';
import { Field } from '../../components/field';
import { Button } from '../../components/button';

// Figma node 748:7317's right-side collage is the same 7 hero token-card images (Figma's own asset
// names repeat every 4 containers), tiled 2-up and looping upward forever — not a static grid. Split
// them into two independent columns so each column's loop period differs slightly from the other's.
const COLLAGE_COLUMNS: readonly (readonly string[])[] = [
  HERO_BOX_IMAGES.filter((_, i) => i % 2 === 0),
  HERO_BOX_IMAGES.filter((_, i) => i % 2 === 1),
];

/** One looping column of the contact page's photo collage: the image list, doubled back-to-back and
 * translated by exactly -50% of its own (doubled) height forever, so the seam is never visible. */
function CollageColumn({ images }: { images: readonly string[] }) {
  const track = [...images, ...images];
  return (
    <div className="relative h-full w-full overflow-hidden">
      <div className="marquee-vertical flex flex-col gap-2">
        {track.map((src, i) => (
          <div key={i} className="relative aspect-square w-full shrink-0 overflow-hidden rounded-12">
            {/* Only the first (visible-on-load) tile of each column is above the fold; the rest
                (including the whole duplicated half of the track) must stay lazy. */}
            <HeroTokenCardFace src={src} size={400} priority={i === 0} cardRadius={12} />
          </div>
        ))}
      </div>
    </div>
  )
}

/** Mobile/tablet stand-in for the desktop collage columns: one horizontally-looping row below the
 * form, same doubled-track technique translated on the X axis instead of Y. */
function CollageRow({ images }: { images: readonly string[] }) {
  const track = [...images, ...images];
  return (
    <div className="relative h-[140px] w-full overflow-hidden sm:h-[180px]">
      <div className="marquee-horizontal flex h-full gap-2">
        {track.map((src, i) => (
          <div key={i} className="relative aspect-square h-full shrink-0 overflow-hidden rounded-12">
            <HeroTokenCardFace src={src} size={400} priority={false} cardRadius={12} />
          </div>
        ))}
      </div>
    </div>
  )
}

const SUPPORT_EMAIL = 'support@proply.io'

export default function ContactUsPage() {
  const [firstName, setFirstName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const trimmedName = firstName.trim()
    const trimmedEmail = email.trim()
    const trimmedMessage = message.trim()

    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      setNotice({ type: 'error', message: 'Please fill in your name, email and message.' })
      return
    }

    const subject = encodeURIComponent(`Message from ${trimmedName}`)
    const body = encodeURIComponent(`${trimmedMessage}\n\n— ${trimmedName} (${trimmedEmail})`)
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`
    setNotice({ type: 'success', message: 'Opening your email app so you can send your message…' })
  }

  return (
    <main className="w-full bg-background-white">
      {/* In flow (pushes content down) below `lg`; from `lg` up it floats over the page instead, so
          the photo collage can start at the true top of the screen instead of being cropped to start
          below the header's own box. The form column's existing lg:pt-[140px] already clears it. */}
      {/* Padding/positioning matches the homepage header (HeroSection.tsx) exactly, so the logo sits
          at the same inset on every page. */}
      <header className="relative z-40 flex w-full shrink-0 items-center max-lg:px-5 max-lg:pb-1 max-lg:pt-[max(1.5rem,env(safe-area-inset-top,0px))] px-[16px] pb-0 pt-[30px] sm:px-[30px] md:px-[40px] md:pt-[40px] lg:absolute lg:inset-x-0 lg:top-0">
        <Link href="/" className="inline-flex items-center">
          <Image
            src="/images/proply-logo.svg"
            alt="Proply.io logo"
            width={136}
            height={38}
            className="inline-block h-[30px] w-auto shrink-0 sm:h-[38px]"
          />
        </Link>
      </header>

      {/* Figma node 748:7317 — a plain centred form on the left, a photo collage bleeding off the
          right edge on desktop. No mobile frame exists for this page, so below `lg` the collage is
          dropped and the form gets the full width, centred, at a comfortable reading measure. The
          Figma frame itself is only 1440px wide, so its 632:808 column split is really "a fixed
          632px content column, then whatever's left" — not a proportion. A `%` width reproduces the
          split only at exactly 1440px; on any wider window it drags a load of dead space along with
          it before the collage even starts, and the two "gaps" (left of the form, and between the
          form and the collage) stop matching. Instead the left column is a fixed, hugging width (its
          true content width — see the padding note below) and the collage's own width comes from
          filling whatever space remains (`lg:left-[742px]`, no explicit width), so it always closes
          that gap by scaling its cards up rather than floating in blank space. The collage is
          positioned absolutely rather than given a CSS Grid column: its own content is an endless
          (doubled) marquee track, which has real, large in-flow height — if it were a normal grid
          item, that height would set the row's auto track size and blow the section out to several
          screens tall. Absolutely positioning it removes it from the height calculation entirely, so
          the section's height comes only from the form column, and the collage just fills (and clips
          to) whatever that turns out to be. */}
      {/* lg:min-h-screen: the section otherwise hugs the form column's height, which is shorter than
          the viewport, so the absolutely-positioned collage (sized to the section) would stop partway
          down the page instead of reaching the bottom of the screen. */}
      <section className="relative w-full lg:min-h-screen">
        {/* lg:w-[742px]: 40px padding + the 662px-wide fields below + 40px padding — a fixed, hugging
            width, not a % of the viewport (see the section-level note above). 662 = 552 (the Figma
            spec) × 1.2 — fields widened 20% per request, which pushes the collage's own start (and
            therefore its cards' scale) out to match. Matches the header's own 40px logo inset on both
            sides, so the gap left of "Contact us" and the gap between the fields and the collage read
            as the same size. */}
        <div className="px-[20px] pb-12 pt-9 sm:px-[40px] sm:pt-12 md:px-[49px] md:pt-16 lg:w-[742px] lg:shrink-0 lg:px-[40px] lg:pb-0 lg:pt-[140px]">
          <div className="mx-auto flex w-full max-w-[662px] flex-col gap-2 lg:mx-0">
            {/* Unbound in Figma (no named text style) — nearest match to the hero headline's own
                unbound heading, so the two largest headings on the site read the same. */}
            <h1 className="ds-text-4xl-medium text-text-primary sm:ds-text-5xl-medium">
              Contact us
            </h1>
            <p className="ds-text-base-normal text-text-tertiary">
              Have a question about Proply? Our team is here to help you get the answers you need.
            </p>

            <form onSubmit={handleSubmit} className="mt-2 flex w-full flex-col items-start gap-1">
              <Field
                variant="tinted"
                align="left"
                height={56}
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
              />
              <Field
                variant="tinted"
                align="left"
                height={56}
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              />
              <Field
                variant="tinted"
                align="left"
                height={112}
                multiline
                placeholder="Message"
                value={message}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
              />

              <Button type="submit" className="mt-2">
                Send message
              </Button>
              {notice ? (
                <p
                  role="status"
                  className={`ds-text-base-normal ${
                    notice.type === 'success' ? 'text-primary-brand' : 'text-red-700'
                  }`}
                >
                  {notice.message}
                </p>
              ) : null}
            </form>
          </div>
        </div>

        {/* Below `lg` there's no room for a side collage, so it drops under the form instead, as a
            single horizontally-looping row. Full-bleed (breaks out of the column's own padding) via
            negative margins, matching the pattern used for the mobile pillar carousel. */}
        <div className="mt-8 -mx-[20px] overflow-hidden sm:-mx-[40px] md:-mx-[49px] lg:hidden">
          <CollageRow images={HERO_BOX_IMAGES} />
        </div>

        {/* The endless vertical loop is clipped to this container's own height, which has no reason
            to land on a tile boundary — without help, whatever card is mid-scroll at that instant
            gets sliced clean in half right above the footer. A bottom fade (not top: the top edge is
            meant to run full-bleed behind the floating header, per the design) turns that hard cut
            into a soft one, so the seam into the footer reads as intentional. */}
        <div
          className="hidden lg:absolute lg:inset-y-0 lg:left-[742px] lg:right-0 lg:grid lg:grid-cols-2 lg:gap-2 lg:overflow-hidden lg:p-2"
          style={{
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 90%, transparent 100%)',
            maskImage: 'linear-gradient(to bottom, black 0%, black 90%, transparent 100%)',
          }}
        >
          {COLLAGE_COLUMNS.map((images, i) => (
            <CollageColumn key={i} images={images} />
          ))}
        </div>
      </section>

      <Footer showContactButton={false} />
    </main>
  )
}
